import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Hammer, CalendarIcon, User } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { PROJECT_STATUS, PROJECT_TYPES } from './constants';

/**
 * RenovationProjectDialog — create/edit renovation-project form.
 * All state is owned by the parent (formData, startDate, endDate,
 * setters) so the dialog can be re-mounted without losing input.
 */
export const RenovationProjectDialog = ({
  open,
  onOpenChange,
  editingProject,
  properties,
  formData,
  setFormData,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onSubmit,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Hammer className="h-5 w-5" />
          {editingProject ? 'Edit Project' : 'New Renovation Project'}
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-4">
        {/* Property & Type */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Property *</Label>
            <Select
              value={formData.property_id}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, property_id: value }))}
            >
              <SelectTrigger data-testid="renovation-property-select">
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties?.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>{prop.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Project Type *</Label>
            <Select
              value={formData.project_type}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, project_type: value }))}
            >
              <SelectTrigger data-testid="renovation-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Title */}
        <div className="space-y-2">
          <Label>Project Title *</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="e.g., Master Bathroom Renovation"
            data-testid="renovation-title-input"
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label>Description</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Describe the scope of work..."
            rows={3}
          />
        </div>

        {/* Budget */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Estimated Budget ($)</Label>
            <Input
              type="number"
              value={formData.estimated_budget}
              onChange={(e) => setFormData((prev) => ({ ...prev, estimated_budget: e.target.value }))}
              placeholder="50000"
              data-testid="renovation-budget-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Actual Cost ($)</Label>
            <Input
              type="number"
              value={formData.actual_cost}
              onChange={(e) => setFormData((prev) => ({ ...prev, actual_cost: e.target.value }))}
              placeholder="0"
            />
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Start Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !startDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(date) => {
                    setStartDate(date);
                    setFormData((prev) => ({
                      ...prev,
                      start_date: date ? format(date, 'yyyy-MM-dd') : '',
                    }));
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Estimated End Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !endDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={(date) => {
                    setEndDate(date);
                    setFormData((prev) => ({
                      ...prev,
                      estimated_end_date: date ? format(date, 'yyyy-MM-dd') : '',
                    }));
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Contractor Info */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Contractor Information
          </Label>
          <div className="grid grid-cols-3 gap-3">
            <Input
              value={formData.contractor_name}
              onChange={(e) => setFormData((prev) => ({ ...prev, contractor_name: e.target.value }))}
              placeholder="Contractor name"
            />
            <Input
              value={formData.contractor_phone}
              onChange={(e) => setFormData((prev) => ({ ...prev, contractor_phone: e.target.value }))}
              placeholder="Phone"
            />
            <Input
              value={formData.contractor_email}
              onChange={(e) => setFormData((prev) => ({ ...prev, contractor_email: e.target.value }))}
              placeholder="Email"
            />
          </div>
        </div>

        {/* Status & Progress */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PROJECT_STATUS).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Progress: {formData.progress_percent}%</Label>
            <Input
              type="range"
              min="0"
              max="100"
              value={formData.progress_percent}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, progress_percent: parseInt(e.target.value) }))
              }
              className="w-full"
            />
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <Label>Notes</Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
            placeholder="Additional notes, milestones, updates..."
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-slate-700 hover:bg-slate-600"
            data-testid="save-renovation-btn"
          >
            {editingProject ? 'Update Project' : 'Create Project'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export default RenovationProjectDialog;
